import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
} from "antd";
import { useEffect, useState, useCallback } from "react";
import {
  ReloadOutlined,
  FilterOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { HEADERS_EXPORT_BEN, POST_MAIN_LAWYER } from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import { ThaiRangePicker } from "../../../hook/ThaiDatePicker";
import MotionHoc from "../../../utils/MotionHoc";
import LawyersCheckingDocuments from "./modal/LawyersCheckingDocuments";

const { Option } = Select;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));

  // States
  const [dataArr, setDataArr] = useState([]);
  const [arrayTable, setArrayTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [datePickerTable, setDatePickerTable] = useState(null);
  const [processStatus, setProcessStatus] = useState(99);
  const [isModalCheckLawyersDoc, setIsModalCheckLawyersDoc] = useState(false);
  const [dataRecord, setDataRecord] = useState(null);

  // ✅ โหลดข้อมูลหลัก
  const loadMain = useCallback(async () => {
    setLoading(true);
    let isMounted = true;
    const controller = new AbortController();

    try {
      const res = await axios.post(
        POST_MAIN_LAWYER,
        { userId: userId },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );

      if (!isMounted) return;

      if (res.data && res.data.length > 0) {
        const filteredData = res.data
          .filter(
            (item) =>
              item.currentStatus === 3 ||
              item.currentStatus === 6 ||
              item.currentStatus === 7,
          )
          .sort((a, b) => {
            const dateA = a.acceptLawyer || a.failDoc;
            const dateB = b.acceptLawyer || b.failDoc;
            if (!dateA && !dateB) return 0;
            if (!dateA) return -1;
            if (!dateB) return 1;

            return new Date(dateA) - new Date(dateB);
          });
        console.log("res.data", res.data);

        setDataArr(filteredData);
        setArrayTable(filteredData);
        console.log("✅ Data loaded:", filteredData.length, "items");
      } else {
        setDataArr([]);
        setArrayTable([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name !== "CanceledError" && isMounted) {
        console.error("❌ Error loading data:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้";
        message.error(errorMsg);
        setDataArr([]);
        setArrayTable([]);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  // ✅ ฟังก์ชันสำหรับสร้าง Options ของ Select
  const renderOptionStatus = useCallback(() => {
    const statusOptions = [
      { value: 99, icon: "📋", label: "ทั้งหมด" },
      { value: 3, icon: "📥", label: "รอรับงาน" },
      { value: 6, icon: "↩️", label: "ตีกลับ" },
      { value: 7, icon: "✅", label: "รับงานแล้ว" },
    ];
    return statusOptions.map(({ value, icon, label }) => (
      <Option key={value} value={value}>
        <span style={{ marginRight: 8 }}>{icon}</span>
        {label}
      </Option>
    ));
  }, []);

  // ✅ ฟังก์ชันกรองข้อมูล
  const handleFilterData = useCallback(() => {
    if (!dataArr || dataArr.length === 0) {
      message.warning("ไม่มีข้อมูลให้กรอง");
      return;
    }

    setLoading(true);

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const isSameDay = (date1, date2) =>
      !!date1 &&
      !!date2 &&
      dayjs(date1).format("YYYY-MM-DD") === dayjs(date2).format("YYYY-MM-DD");

    const isInRange = (date, [from, to]) => {
      if (!date || !from || !to) return false;
      const timestamp = dayjs(date).valueOf();
      const start = dayjs(from).startOf("day").valueOf();
      const end = dayjs(to).endOf("day").valueOf();
      return timestamp >= start && timestamp <= end;
    };

    const searchQuery = normalize(searchInputTable);

    const result = dataArr.filter((item) => {
      // 1️⃣ กรองตามข้อความค้นหา
      const passQuery =
        !searchQuery ||
        [
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      // 2️⃣ กรองตามวันที่
      let passDate = true;
      if (datePickerTable) {
        const dateToCheck = item.completeCheckDoc;
        passDate =
          Array.isArray(datePickerTable) && datePickerTable.length === 2
            ? isInRange(dateToCheck, datePickerTable)
            : isSameDay(dateToCheck, datePickerTable);
      }

      // 3️⃣ กรองตามสถานะ
      let passStatus = true;

      if (processStatus !== 99 && processStatus != null) {
        if (typeof processStatus === "string" && processStatus.includes("-")) {
          // ⭐ แยก currentStatus และ processValue
          const [currentStr, processStr] = processStatus.split("-");
          const current = Number(currentStr);

          // ⭐ จัดการกับ "null" string โดยเฉพาะ
          let processValue;
          if (processStr === "null") {
            processValue = null;
          } else {
            processValue = Number(processStr);
          }

          console.log("Filter - current:", current, "process:", processValue);
          console.log(
            "Item - currentStatus:",
            item.currentStatus,
            "currentProcess:",
            item.currentProcess,
          );

          // ⭐ เช็คทั้ง currentStatus และ currentProcess
          passStatus =
            item.currentStatus === current &&
            (processValue === null
              ? item.currentProcess == null // เช็คทั้ง null และ undefined
              : item.currentProcess === processValue);
        } else {
          // กรณีเลือกแค่ currentStatus (3 หรือ 5)
          passStatus = item.currentStatus === Number(processStatus);
        }
      }

      return passQuery && passDate && passStatus;
    });

    setArrayTable(result);

    setTimeout(() => {
      setLoading(false);
      if (result.length === 0) {
        message.warning("ไม่พบข้อมูลที่ตรงกับเงื่อนไข");
      } else {
        message.success(`พบข้อมูล ${result.length} รายการ`);
      }
    }, 250);
  }, [dataArr, searchInputTable, datePickerTable, processStatus]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setDatePickerTable(null);
    setSearchInputTable("");
    setProcessStatus(99);
    setArrayTable(dataArr);
    message.info("ล้างตัวกรองสำเร็จ");
  }, [dataArr]);

  // ✅ Callback จาก Modal
  const handleModalCallback = useCallback(() => {
    console.log("📥 Callback from modal");
    loadMain();
  }, [loadMain]);

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "center",
      render: (_, record) => (
        <span>
          {`${record.SNAM || ""}${record.NAME1 || ""} ${
            record.NAME2 || ""
          }`.trim()}
        </span>
      ),
    },
    {
      title: "วันตรวจเอกสารสำเร็จ",
      dataIndex: "completeCheckDoc",
      key: "completeCheckDoc",
      align: "center",
      width: 160,
      render: (date) =>
        date ? dayjs(date).add(543, "year").format("D MMM YY") : "-",
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      width: 140,
      render: (_, record) => {
        const getStatus = () => {
          const { currentStatus, loanActive } = record;

          // 1. เช็คสถานะ "ปิดสัญญา" ก่อนเป็นอันดับแรก (ใช้ if คลีนสุดครับ)
          if (loanActive === 0) {
            return { color: "default", label: "ปิดสัญญา" };
          }

          // 2. ถ้ายังไม่ปิดสัญญา ค่อยมาเช็ค currentStatus ตามปกติ
          if (currentStatus === 3) {
            return { color: "red", label: "รอรับงาน" };
          }

          if (currentStatus === 6) {
            return {
              color: "orange",
              label: (
                <>
                  ตีกลับ
                  <br />
                  {record.failDoc &&
                    dayjs(record.failDoc).add(543, "year").format("D MMM YY")}
                </>
              ),
            };
          }

          if (currentStatus === 7) {
            return {
              color: "green",
              label: (
                <>
                  รับงานแล้ว
                  <br />
                  {record.acceptLawyer &&
                    dayjs(record.acceptLawyer)
                      .add(543, "year")
                      .format("D MMM YY")}
                </>
              ),
            };
          }

          // 3. ถ้าไม่เข้าเงื่อนไขอะไรเลย
          return { color: "default", label: "ไม่ระบุ" };
        };

        const status = getStatus();

        return (
          <Tag
            color={status.color}
            style={{ display: "inline-block", textAlign: "center" }}
          >
            {status.label}
          </Tag>
        );
      },
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 150,
      render: (_, record) => {
        const { currentStatus, processStatus, currentProcess } = record;

        // ⭐ ใช้ currentProcess แทน processStatus
        const process = currentProcess ?? processStatus;

        if (currentStatus === 3) {
          return (
            <Button
              color="red"
              variant="outlined"
              // disabled={!record.loanActive}
              onClick={() =>
                // handleAcceptWork(record)
                {
                  setDataRecord(record);
                  setIsModalCheckLawyersDoc(true);
                }
              }
            >
              📥 รับงาน
            </Button>
          );
        }
        if (currentStatus === 6) {
          return (
            <Button
              color="orange"
              variant="outlined"
              icon={<EditOutlined />}
              disabled={!record.loanActive}
              onClick={() => {
                setDataRecord(record);
                setIsModalCheckLawyersDoc(true);
              }}
            >
              แก้ไข
            </Button>
          );
        }
        return <span>-</span>;
      },
    },
  ];

  return (
    <>
      <Card>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row gutter={[0, 16]}>
            {/* Filters */}
            <Col span={24}>
              <Space size={10} wrap>
                <ThaiRangePicker
                  value={datePickerTable}
                  onChange={setDatePickerTable}
                  size="large"
                  placeholder={["เริ่มต้น", "สิ้นสุด"]}
                />
                <Input
                  placeholder="ค้นหาเลขสัญญา, ชื่อ, เลขบัตร"
                  value={searchInputTable}
                  onChange={(e) => setSearchInputTable(e.target.value)}
                  onPressEnter={handleFilterData}
                  style={{ width: 250, borderRadius: 8 }}
                  size="small"
                  allowClear
                  suffix={<span style={{ marginRight: 5 }}>🔍</span>}
                />
                <Select
                  size="large"
                  style={{ minWidth: 180 }}
                  placeholder="เลือกสถานะ"
                  value={processStatus}
                  onChange={setProcessStatus}
                  showSearch
                  optionFilterProp="children"
                >
                  {renderOptionStatus()}
                </Select>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  size="large"
                  onClick={handleFilterData}
                  disabled={!dataArr || dataArr.length === 0}
                  style={{ borderRadius: 8 }}
                >
                  กรองข้อมูล
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  size="large"
                  onClick={handleResetFilter}
                  disabled={!dataArr || dataArr.length === 0}
                  style={{ borderRadius: 8 }}
                >
                  ล้าง
                </Button>
              </Space>
            </Col>

            {/* Table */}
            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={arrayTable}
                rowKey={(record) => record.CONTNO || record.id}
                scroll={{ x: 1000 }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                footer={() => (
                  <div style={{ textAlign: "left", fontWeight: 500 }}>
                    จำนวนสัญญาทั้งหมด: {arrayTable?.length || 0} รายการ
                  </div>
                )}
              />
            </Col>
          </Row>
        </Spin>
      </Card>

      {isModalCheckLawyersDoc && (
        <LawyersCheckingDocuments
          open={isModalCheckLawyersDoc}
          close={() => setIsModalCheckLawyersDoc(false)}
          dataDefault={dataRecord}
          funcUpdateStatus={handleModalCallback}
        />
      )}
    </>
  );
};

const ApproveDoc = MotionHoc(Main);
export default ApproveDoc;
